import React from "react";
import { Link } from "react-router-dom";
import FadeIn from "../components/FadeIn";
import { IMAGES } from "../lib/content";

const FOUNDER_PHOTO =
  "https://customer-assets.emergentagent.com/job_luxury-journeys-jp/artifacts/fulp0mjv_l7gl0757_Profile%20photo%202.jpg";

const About = () => {
  return (
    <main className="pt-28 md:pt-36" data-testid="about-page">
      <section className="py-20 md:py-28 border-b border-white/5" data-testid="name-origin-section">
        <div className="container-editorial">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start">
            <FadeIn className="md:col-span-4">
              <div className="overline">Name origin</div>
              <h2 className="mt-5 font-serif text-4xl md:text-5xl text-bone tracking-tight leading-[1.05]">
                Why <span className="italic text-gold">Mensorio</span>
              </h2>
              <div className="mt-6 rule" />
            </FadeIn>

            <FadeIn delay={140} className="md:col-span-7 md:col-start-6">
              <p className="font-serif text-3xl md:text-[2.4rem] text-bone leading-[1.25] tracking-tight">
                Drawn from <span className="italic text-gold">mensore</span> — the Okinawan welcome.
              </p>
              <p className="mt-8 text-bone/70 font-light text-lg leading-relaxed max-w-xl">
                Less a greeting than a posture: to receive a guest entirely, before anything is asked.
              </p>
            </FadeIn>
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 border-b border-white/5" data-testid="founder-section">
        <div className="container-editorial grid grid-cols-1 md:grid-cols-12 gap-10 md:gap-16 items-start">
          <FadeIn className="md:col-span-5">
            <div className="relative overflow-hidden h-[520px] md:h-[720px] bg-ink-surface" data-testid="founder-photo">
              <img
                src={FOUNDER_PHOTO}
                alt="Taiga Kunii, founder of Mensorio"
                className="absolute inset-0 w-full h-full object-cover object-center grayscale-[15%]"
                loading="lazy"
              />
              <div className="absolute inset-0 ring-1 ring-inset ring-gold/20 pointer-events-none" />
              <div className="absolute left-4 bottom-4 overline text-[10px] bg-ink/60 backdrop-blur px-3 py-2">
                Taiga Kunii &nbsp;·&nbsp; Tokyo
              </div>
            </div>
          </FadeIn>

          <FadeIn delay={120} className="md:col-span-7">
            <div className="overline mb-5">Founder</div>
            <h2 className="font-serif text-4xl md:text-5xl text-bone tracking-tight leading-[1.05]">
              Taiga Kunii
            </h2>
            <div className="mt-3 text-bone/55 font-light text-sm tracking-[0.18em] uppercase">
              Producer · Tokyo
            </div>
            <div className="mt-6 rule" />

            <div className="mt-8 space-y-6 text-bone/75 font-light leading-relaxed text-[1.02rem]">
              <p>
                Taiga Kunii is a Tokyo-based producer working at the intersection of music, entertainment, and intellectual property.
              </p>
              <p>
                For years, he has operated inside Japan's entertainment industry — producing projects, building global partnerships, and bringing international artists and executives into Japan. Through this work, he developed something most travel services cannot offer:
                <span className="text-bone"> real access.</span>
              </p>
              <p>
                Mensorio was not built as a travel business first.
                It began as a private service — designed for artists, executives, and global partners who needed Japan to work seamlessly at the highest level.
              </p>
              <p className="font-serif italic text-gold text-xl leading-snug">
                What started as a necessity became a standard.
              </p>
              <p>
                Having lived in <span className="text-bone">Kenya, Brazil, the United States, and Myanmar,</span> Taiga brings a global perspective to what luxury service should feel like: intuitive, precise, and quietly exceptional.
              </p>
              <p>
                Mensorio is an extension of that philosophy — designed for those who don't just want to visit Japan, but to experience it with
                <span className="text-bone"> clarity, access, and intention.</span>
              </p>
            </div>

            <div className="mt-12 flex flex-wrap items-center gap-x-10 gap-y-4">
              <a
                href="https://www.linkedin.com/in/taiga-kunii-9a153280/?locale=ja"
                target="_blank"
                rel="noopener noreferrer"
                className="link-underline text-[12px] tracking-[0.22em] uppercase text-bone/70 hover:text-bone"
                data-testid="about-linkedin"
              >
                Connect on LinkedIn →
              </a>
              <Link
                to="/plan"
                className="link-underline text-[12px] tracking-[0.22em] uppercase text-gold hover:text-gold-light"
                data-testid="about-founder-cta"
              >
                Begin a conversation →
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>

      <section className="py-24 md:py-32 border-b border-white/5">
        <div className="container-editorial">
          <div className="grid grid-cols-1 md:grid-cols-3 border-t border-l border-white/10">
            {[
              { t: "Selective", d: "Fewer clients. More time per itinerary. Better results." },
              { t: "Discreet", d: "Private communication, private reservations, private access where it matters." },
              { t: "Personal", d: "One point of contact — the founder — from inquiry through return." },
            ].map((v, i) => (
              <FadeIn key={v.t} delay={i * 120}>
                <div className="p-10 md:p-14 border-r border-b border-white/10 min-h-[260px]" data-testid={`value-${v.t.toLowerCase()}`}>
                  <div className="font-serif text-gold text-5xl">0{i + 1}</div>
                  <div className="mt-6 font-serif text-2xl text-bone tracking-tight">{v.t}</div>
                  <p className="mt-4 text-bone/60 font-light leading-relaxed">{v.d}</p>
                </div>
              </FadeIn>
            ))}
          </div>
        </div>
      </section>

      <section className="py-32 md:py-44">
        <div className="container-editorial text-center max-w-3xl mx-auto">
          <FadeIn>
            <h2 className="font-serif text-4xl md:text-6xl text-bone tracking-tight leading-[1.05]">
              Every journey begins with <span className="italic text-gold">a conversation.</span>
            </h2>
          </FadeIn>
          <FadeIn delay={180}>
            <div className="mt-12">
              <Link
                to="/plan"
                className="inline-flex items-center gap-4 bg-gold text-ink px-10 py-5 text-[12px] tracking-[0.28em] uppercase font-medium hover:bg-gold-light transition-colors duration-500"
                data-testid="about-cta"
              >
                Plan Your Journey
                <span className="w-8 h-px bg-ink/60" />
              </Link>
            </div>
          </FadeIn>
        </div>
      </section>
    </main>
  );
};

export default About;
